/**
 * 全局实时语音播报控制器
 *
 * 类似豆包顶部喇叭开关：
 * - 开关打开后，AI 流式生成的文字会逐句实时朗读
 * - 关闭时立即停止当前播报
 * - 用户发送新消息、手动停止生成、切换会话时重置
 */
import axios from "axios";
import VoiceToTextSDK from "./core/VoiceToTextSDK";
import { PROVIDER_TYPE } from "./constants/ProviderConstants";
import { ALIYUN_CONFIG, COSYVOICE_VOICE_XU, COSYVOICE_VOICE_XU_SPEECH_RATE } from "./constants/AliyunConstants";
import { APP_KEY } from "../audio/utils/constant";
import messageTTS from "./MessageTTS";

const DEFAULT_STORAGE_KEY = "global_streaming_tts_enabled";

async function fetchAliyunToken() {
  try {
    const res = await axios.get(`${window.PUBLIC_URL}/medagent/voice/getToken`);
    if (res.data?.code !== 200 || !res.data?.data?.token) {
      throw new Error(res.data?.msg || "获取语音 token 失败");
    }
    return {
      token: res.data.data.token,
      appKey: APP_KEY,
    };
  } catch (err) {
    console.error("[StreamingTTSController] fetch token failed:", err);
    throw err;
  }
}

export default class StreamingTTSController {
  constructor(options = {}) {
    // 支持按智能体/页面隔离开关状态：传入 storageKey 则各自独立记忆，缺省保持全局共用
    this.storageKey = options.storageKey || DEFAULT_STORAGE_KEY;
    this.enabled = this._loadEnabledState();
    // 初始化时同步 MessageTTS 的自动播放标记，避免 localStorage 中开关为 ON 时不同步
    messageTTS.autoPlayEnabled = this.enabled;
    // 注册自身到 MessageTTS，以便切换点击消息时停掉流式播报，避免双音
    messageTTS.registerStreamingTTS(this);
    this.active = false;
    this.error = null;
    this.sdk = null;
    this.initPromise = null;
    this.voiceOptions = options;
    this._stateListeners = new Set();
    // 按住说话时跳过当前回答剩余文本的播报，但不关闭总开关
    this._skipCurrentResponse = false;
    // 待点亮喇叭的消息 ID：发送新消息时记录，仅当首个实际流式内容 chunk 到达时才消费点亮，
    // 避免消息占位（思考中）阶段就显示自动播报状态
    this._pendingSpeakId = null;
    // 流式输出的最新完整文本（无论开关状态都记录，供重新打开开关时从头朗读）
    this._latestFullText = "";
    // 已投喂给 SDK 的文本基线（用于计算增量投喂）
    this._playedText = "";
    // 打开开关时无文本（思考中）：等待首个文本包到达时从头朗读
    this._pendingRestartFromHead = false;
    // 从头朗读进行中（SDK 初始化未完成）：期间暂不投喂增量，避免重复
    this._restarting = false;
    // 当前回复是否已结束（finish 已调用），用于从头朗读后补投增量时 flush 缓冲
    this._finished = false;
    // 当前流式消息 ID：关闭开关时会清除 messageTTS 的 streamingMessageId，
    // 重开开关从头朗读时用它恢复喇叭高亮（"流式输出中"状态）
    this._streamingMsgId = null;
  }

  /**
   * 初始化：拉取 token 并创建 SDK。
   * 需要在组件 mount 后调用一次。
   */
  async init() {
    if (this.sdk) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._doInit();
    try {
      await this.initPromise;
    } finally {
      this.initPromise = null;
    }
  }

  async _doInit() {
    try {
      const { token, appKey } = await fetchAliyunToken();

      const selectedVoice = this.voiceOptions.voice || ALIYUN_CONFIG.VOICE;
      console.log('[StreamingTTSController] using voice:', selectedVoice, 'voiceOptions:', this.voiceOptions);
      this.sdk = new VoiceToTextSDK({
        provider: PROVIDER_TYPE.ALIYUN,
        voice: selectedVoice,
        sampleRate: ALIYUN_CONFIG.SAMPLE_RATE,
        format: ALIYUN_CONFIG.FORMAT,
        token,
        appKey,
        // 许鹏飞医生音色使用专属语速，其余音色保持默认
        ...(selectedVoice === COSYVOICE_VOICE_XU && { speechRate: COSYVOICE_VOICE_XU_SPEECH_RATE }),
      });

      // 流式播放完成时，清除高亮状态
      this.sdk.onPlaybackComplete(() => {
        messageTTS.clearStreamingMessageId();
      });
    } catch (err) {
      this.error = err;
      this._notifyStateListeners();
      throw err;
    }
  }

  _ensureInitialized() {
    if (!this.sdk) {
      console.warn("[StreamingTTSController] SDK 尚未初始化，请先调用 init()");
    }
  }

  _loadEnabledState() {
    try {
      return localStorage.getItem(this.storageKey) === "1";
    } catch {
      return false;
    }
  }

  _saveEnabledState(value) {
    try {
      localStorage.setItem(this.storageKey, value ? "1" : "0");
    } catch {
      // ignore
    }
  }

  _notifyStateListeners() {
    const state = this.getState();
    this._stateListeners.forEach((fn) => fn(state));
  }

  /** 获取当前开关状态 */
  isEnabled() {
    return this.enabled;
  }

  /** 设置开关状态 */
  setEnabled(enabled) {
    const next = Boolean(enabled);
    if (this.enabled === next) {
      // 状态未变化：若为关闭，仍强制停止一切播报，保证"关闭即停止"语义
      if (!next) {
        this.stop();
        messageTTS.stop();
        // 清除流式播报高亮，避免再次开启时喇叭残留
        messageTTS.clearStreamingMessageId();
      }
      return;
    }
    this.enabled = next;
    this._saveEnabledState(next);
    this._notifyStateListeners();
    // 切换开关时清空"待点亮"标记：重新开启后需发送新消息才会再次点亮喇叭
    this._pendingSpeakId = null;
    // 同步 MessageTTS 自动播放标记
    messageTTS.autoPlayEnabled = next;
    if (!next) {
      this.stop();
      // 关闭全局播报时，同步停止所有单条消息的播报（参考豆包）
      messageTTS.stop();
      // 清除流式播报消息 ID，保证"关闭再开启"后喇叭保持关闭，直到发送新消息
      messageTTS.clearStreamingMessageId();
    } else {
      // 重新打开开关：流式已结束时不再播报旧消息，仅新消息流式输出时播报；其余情况见 _onEnabled
      this._onEnabled();
    }
  }

  /** 切换开关 */
  toggle() {
    this.setEnabled(!this.enabled);
    return this.enabled;
  }

  /** 订阅状态变化，返回取消订阅函数 */
  addStateListener(fn) {
    this._stateListeners.add(fn);
    fn(this.getState());
    return () => this._stateListeners.delete(fn);
  }

  getState() {
    return {
      enabled: this.enabled,
      active: this.active,
      error: this.error,
    };
  }

  /**
   * 流式文本投喂入口（每次传入截至当前的完整累计文本）。
   *
   * - 无论开关状态，都记录最新完整文本，供重新打开开关时从头朗读；
   * - 开关开启时，基于已播文本计算增量投喂，实现无缝衔接；
   * - 打开开关时已有文本则立即从头朗读；思考中则首个文本包到达时从头朗读；
   * - 播到一半关闭再开启：从头朗读最新完整内容，后续增量无缝衔接。
   */
  feedFullText(fullText) {
    if (!fullText) return;
    this._latestFullText = fullText;
    if (!this.enabled) return;
    // 按住说话跳过当前回答时，忽略投喂
    if (this._skipCurrentResponse) return;

    // 打开开关时处于思考中：首个文本包到达时从头朗读
    if (this._pendingRestartFromHead) {
      this._pendingRestartFromHead = false;
      this._restartFromLatest();
      return;
    }

    // 从头朗读进行中：等待 restart 完成，后续文本包会基于新基线计算增量
    if (this._restarting) return;

    const prev = this._playedText;
    const delta = fullText.startsWith(prev)
      ? fullText.slice(prev.length)
      : fullText;
    if (!delta) return;

    this._playedText = fullText;
    this._append(delta);
  }

  /**
   * 流式追加增量文本（兼容旧调用；推荐使用 feedFullText 传入完整累计文本）
   */
  appendText(text) {
    if (!this.enabled || !text) return;
    // 按住说话被打断后，忽略当前回答剩余文本，但不影响总开关
    if (this._skipCurrentResponse) return;
    this._append(String(text));
  }

  /** 内部：将增量文本追加到 SDK */
  async _append(delta) {
    try {
      await this.init();
    } catch {
      return;
    }
    if (!this.sdk) return;

    // 首次或恢复播放时，先启动 SDK 播放状态
    if (!this.sdk.isPlaying()) {
      this.sdk.play();
    }

    this.sdk.appendText(String(delta));
    // 实际播报发生时确保喇叭点亮（首个流式内容、增量续播、从头朗读）；
    // 首个 chunk 到达时才消费待点亮 ID，避免消息占位（思考中）阶段就显示自动播报状态
    this._markStreamingActive();
  }

  /**
   * 打开开关时：
   * - 流式输出已结束（finish 已调用）：不播报已输出的消息，仅新消息流式输出时播报；
   * - 流式输出中且已有文本：立即从头朗读，后续增量无缝衔接；
   * - 思考中（尚无文本）：等首个文本包到达时从头朗读。
   */
  _onEnabled() {
    // 流式输出已结束：已输出的消息不再播报（用户已看完），只有新的流式消息才播报
    if (this._finished) {
      this.stop();
      return;
    }
    if (this._latestFullText) {
      this._restartFromLatest();
    } else {
      this._pendingRestartFromHead = true;
    }
  }

  /**
   * 从头朗读最新完整内容。
   * 同步更新增量基线，避免并发的增量投喂重复/丢失。
   */
  async _restartFromLatest() {
    const text = this._latestFullText;
    if (!text) {
      this._pendingRestartFromHead = true;
      return;
    }
    // 同步更新基线：从头朗读期间到达的新包不会重复投喂已朗读内容
    this._playedText = text;
    this._restarting = true;
    try {
      await this.init();
    } catch {
      this._restarting = false;
      return;
    }
    if (!this.sdk) {
      this._restarting = false;
      return;
    }
    try {
      this.sdk.restart(text);
    } catch (e) {
      // restart 失败时恢复投喂基线，避免后续流式文本被永久跳过
      console.error("[TTS] 从头朗读失败", e);
      this._restarting = false;
      return;
    }
    this._restarting = false;
    // 从头朗读时点亮喇叭
    this._markStreamingActive();
    // 从头朗读期间若有新文本包到达（流式未结束），补投增量，实现无缝衔接
    this._flushDeltaAfterRestart();
  }

  /** 从头朗读完成后，补投 restart 期间到达的新增文本 */
  async _flushDeltaAfterRestart() {
    const latest = this._latestFullText;
    const prev = this._playedText;
    if (!latest || latest === prev) return;
    const delta = latest.startsWith(prev)
      ? latest.slice(prev.length)
      : latest;
    if (!delta) return;
    this._playedText = latest;
    await this._append(delta);
    // restart 期间流式已结束：补投后 flush，避免末尾文本滞留缓冲
    if (this._finished) {
      this.sdk?.finish();
    }
  }

  /** 实际开始播报（首个流式内容或从头朗读）时点亮喇叭 */
  _markStreamingActive() {
    const pendingId = this.consumePendingStreamingMessageId();
    // 优先使用待点亮的消息 ID；重开开关时 pendingId 可能已被消费，
    // 则用当前流式消息 ID 恢复"流式输出中"的喇叭高亮
    const msgId = pendingId || this._streamingMsgId;
    if (msgId) {
      messageTTS.setStreamingMessageId(msgId);
    }
  }

  /**
   * 通知当前回复已结束，让 TTS 把剩余缓冲读完
   */
  finish() {
    this._finished = true;
    this.sdk?.finish();
  }

  /**
   * 立即停止当前播报并清空缓冲
   */
  stop() {
    this.sdk?.stop();
  }

  /**
   * 按住说话时调用：立即停止当前播报，并跳过当前回答剩余文本。
   * 总开关保持原样，发送新消息后自动恢复。
   */
  skipCurrentResponse() {
    this.stop();
    this._skipCurrentResponse = true;
  }

  /** 当前回答是否被按住说话跳过（跳过期间不应点亮喇叭） */
  isSkippingCurrentResponse() {
    return this._skipCurrentResponse;
  }

  /**
   * 记录待点亮喇叭的消息 ID（发送新消息时调用）。
   * 仅在首个实际流式内容 chunk 到达时才被消费点亮。
   */
  armStreamingMessageId(msgId) {
    this._pendingSpeakId = msgId || null;
    // 记住当前流式消息 ID：仅当设置非空 ID 时更新，供重开开关从头朗读时恢复喇叭高亮；
    // 空 ID（请求结束清理）只清待点亮标记，_streamingMsgId 交由 reset()（新消息时）清理
    if (msgId) {
      this._streamingMsgId = msgId;
    }
  }

  /** 消费并返回待点亮的消息 ID（同时清空标记） */
  consumePendingStreamingMessageId() {
    const id = this._pendingSpeakId;
    this._pendingSpeakId = null;
    return id;
  }

  /**
   * 重置：停止播报、清空缓冲、清除错误
   */
  reset() {
    this.sdk?.reset();
    this.active = false;
    this.error = null;
    this._skipCurrentResponse = false;
    this._pendingSpeakId = null;
    // 新消息开始时重置流式播报基线
    this._latestFullText = "";
    this._playedText = "";
    this._pendingRestartFromHead = false;
    this._restarting = false;
    this._finished = false;
    this._streamingMsgId = null;
    // 发送新消息时，同步停止所有单条消息的手动播报，避免与流式 TTS 产生双音
    messageTTS.stop();
    messageTTS.clearStreamingMessageId();
    this._notifyStateListeners();
  }
}
